// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import {
  formatDateTime,
  formatDuration,
  formatPhoneNumber,
  getLatestRidehail,
  getDriverInfo,
  getRideDurationMinutes,
  isFailed,
} from '../utils/guestRideUtils';
import { approvalStateOf, guestRideActionsFor } from '../utils/rideActions';
import GuestRideApprovalNote from './GuestRideApprovalNote';
import GuestRideStatusBadge from '@components/Badges/GuestRideStatusBadge';

import { Avatar, Box, Card, CardContent, Typography, Button, IconButton, Tooltip } from '@mui/material';
import { BiSolidPhoneCall, BiSolidXCircle, BiSolidEdit, BiSolidTrash, BiLinkAlt, BiLinkExternal, BiUndo } from 'react-icons/bi';

import CancelRideModal from './CancelRideModal';
import DeleteRideModal from './DeleteRideModal';
import RevertRideModal from './RevertRideModal';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import CallRideModal from './CallRideModal';
import GuestRideModal from './GuestRideModal';
import GuestRideDetailsModal from './GuestRideDetailsModal';
import GuestLinkModal from './GuestLinkModal';
import GuestRideProviderMark from './GuestRideProviderMark';
import { useRooftop } from '@store';

// TYPES ---------------------------------------------------------------- //

interface GuestRideCardProps {
  ride: any;
  highlightedRideId?: number | null;
  onHighlightClear?: () => void;
}

/** Label + bold value for the grey info box detail row; optional small subline below the value. */
const DetailStat = ({ label, value, subValue }: { label: string; value: string; subValue?: string }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        color: 'text.secondary',
        fontSize: 10,
        fontWeight: 500,
        lineHeight: 1.25,
        mb: 0.25,
        textAlign: 'center',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500, lineHeight: 1.25, textAlign: 'center' }}>
      {value}
    </Typography>
    {subValue ? (
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 10,
          fontWeight: 500,
          lineHeight: 1.25,
          mt: 0.25,
          textAlign: 'center',
        }}
      >
        {subValue}
      </Typography>
    ) : null}
  </Box>
);

// COMPONENT ---------------------------------------------------------------- //

const GuestRideCard: React.FC<GuestRideCardProps> = ({ ride, highlightedRideId, onHighlightClear }) => {
  // Managing a ride (cancel/edit/delete/revert) gates on the Guest Rides product's capability.
  const canManage = capabilityGate(useProductEntry('guest-rides'), 'manageExisting').kind === 'render';
  const rooftop = useRooftop();

  const [cancelModalOpen, setCancelModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [revertModalOpen, setRevertModalOpen] = React.useState(false);
  const [callModalOpen, setCallModalOpen] = React.useState(false);
  const [retryModalOpen, setRetryModalOpen] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [shareModalOpen, setShareModalOpen] = React.useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);

  const ridehail = getLatestRidehail(ride);
  const driverInfo = getDriverInfo(ride);
  const durationMinutes = getRideDurationMinutes(ride);
  const failed = isFailed(ride);
  const actions = guestRideActionsFor(ride, canManage);
  const canEdit = actions.edit;
  const canDelete = actions.delete;
  const canRevert = actions.revert;
  const canCancel = actions.cancel || actions.cancelRequest;
  const isHighlighted = highlightedRideId === ride?.id;

  React.useEffect(() => {
    if (isHighlighted && onHighlightClear) {
      const timer = setTimeout(() => onHighlightClear(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted, onHighlightClear]);

  const detailSegments = React.useMemo(() => {
    const segments: { label: string; value: string; subValue?: string }[] = [];
    if (ridehail?.created_at) {
      segments.push({ label: 'Time Called', value: formatDateTime(ridehail.created_at) });
    }
    if (ridehail?.pickup_at) {
      segments.push({
        label: 'ETA',
        value: formatDateTime(ridehail.pickup_at),
      });
    }
    if (durationMinutes != null) {
      segments.push({
        label: 'Duration',
        value: formatDuration(durationMinutes),
      });
    }
    return segments;
  }, [ridehail?.created_at, ridehail?.pickup_at, durationMinutes]);

  return (
    <Box sx={{ height: '100%', minWidth: 400, maxWidth: 960, display: 'flex', flexDirection: 'column' }}>
      <Card
        onClick={() => setDetailsModalOpen(true)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          cursor: 'pointer',
          borderRadius: '12px',
          ...(failed
            ? { backgroundColor: 'rgba(255,32,64,0.06)', border: '1px solid', borderColor: 'error.light' }
            : {}),
          boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
          transition: 'all 200ms ease, box-shadow 300ms ease',
          animation: isHighlighted ? 'highlightPulse 1.5s ease-in-out 2' : 'none',
          '@keyframes highlightPulse': {
            '0%, 100%': {
              boxShadow: '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',
            },
            '50%': {
              boxShadow: '0px 0px 0px 3px rgba(255,72,48,0.3), 0px 4px 12px rgba(0,0,0,0.08)',
            },
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0px 8px 24px rgba(0,0,0,0.10), 0px 4px 8px rgba(0,0,0,0.05)',
          },
        }}
      >
        <CardContent
          sx={{
            p: 2.5,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            '&:last-child': { pb: 2.5 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Zone 1 — Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1.5 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant='h4'
                  component='h3'
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: 'text.primary',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {ride?.guest_name || 'Unknown Guest'}
                </Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                  {formatPhoneNumber(ride?.guest_phone)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                <GuestRideProviderMark provider={ridehail?.provider || ride?.provider} />
                <GuestRideStatusBadge status={ride?.status} />
              </Box>
            </Box>

            {/* Zone 2 — Route */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5, flexShrink: 0 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'utility.main' }} />
                <Box sx={{ width: 2, height: 17, bgcolor: 'divider', my: 0.25 }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'utility.lighter' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' sx={{ lineHeight: 1.25 }}>
                  {ride?.pickup_address || 'Pickup address pending'}
                </Typography>
                <Typography variant='body2' sx={{ mt: 1.75, lineHeight: 1.25 }}>
                  {ride?.dropoff_address || 'Dropoff address pending'}
                </Typography>
              </Box>
            </Box>

            {/* Zone 3 — Stats in soft surface (only when there is at least one detail) */}
            {detailSegments.length > 0 ? (
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: 0,
                  }}
                >
                  {detailSegments.map((seg, index) => (
                    <DetailStat
                      key={`${seg.label}-${index}`}
                      label={seg.label}
                      value={seg.value}
                      subValue={seg.subValue}
                    />
                  ))}
                </Box>
              </Box>
            ) : null}

          </Box>

          {/* Zone 4 — Driver + actions (pinned to card bottom) */}
          <Box sx={{ marginTop: 'auto', flexShrink: 0, minWidth: 0, pt: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {driverInfo?.name ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {driverInfo.photoUrl ? (
                      <Box sx={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
                        <Avatar
                          src={driverInfo.photoUrl}
                          alt={driverInfo.name}
                          imgProps={{ loading: 'lazy' }}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            boxShadow: '0px 0px 6px 2px #00000030 inset',
                            pointerEvents: 'none',
                          }}
                        />
                      </Box>
                    ) : null}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant='body2' noWrap sx={{ fontWeight: 600, lineHeight: 1.25, mb: 0.25 }}>
                        {driverInfo.name}
                      </Typography>
                      {driverInfo.vehicle ? (
                        <Typography variant='caption' noWrap sx={{ color: 'text.secondary', lineHeight: 1.25 }}>
                          {driverInfo.vehicle}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                ) : failed && ridehail?.quality_rejection_reason ? (
                  <Typography
                    variant='caption'
                    sx={{ color: 'error.main', display: 'block', lineHeight: 1.3, wordBreak: 'break-word' }}
                  >
                    {ridehail.quality_rejection_reason}
                  </Typography>
                ) : approvalStateOf(ride) === 'waiting' || approvalStateOf(ride) === 'denied' ? (
                  <GuestRideApprovalNote ride={ride} canResend={actions.resend} />
                ) : (
                  <Typography variant='caption' noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                    No driver assigned
                  </Typography>
                )}
              </Box>

              <Box
                onClick={e => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                {actions.call && (
                  <Button
                    size='small'
                    variant='contained'
                    color='primary'
                    startIcon={<BiSolidPhoneCall size={16} />}
                    onClick={() => {
                      if (actions.call === 'retry-edit') setRetryModalOpen(true);
                      else setCallModalOpen(true);
                    }}
                    sx={{ mr: 1 }}
                  >
                    {actions.call === 'call' ? 'Call Ride' : 'Retry Ride'}
                  </Button>
                )}

                {canCancel && (
                  <Button
                    size='small'
                    variant='contained'
                    color='error'
                    startIcon={<BiSolidXCircle size={16} />}
                    onClick={() => setCancelModalOpen(true)}
                  >
                    {actions.cancelRequest ? 'Cancel Request' : 'Cancel Ride'}
                  </Button>
                )}

                <Tooltip title='Share guest link'>
                  <IconButton onClick={() => setShareModalOpen(true)}>
                    <BiLinkAlt size={20} />
                  </IconButton>
                </Tooltip>

                {!canEdit && (
                  <Tooltip title='View details'>
                    <IconButton onClick={() => setDetailsModalOpen(true)}>
                      <BiLinkExternal size={20} />
                    </IconButton>
                  </Tooltip>
                )}

                {canEdit && (
                  <Tooltip title='Edit ride'>
                    <IconButton onClick={() => setEditModalOpen(true)}>
                      <BiSolidEdit size={20} />
                    </IconButton>
                  </Tooltip>
                )}

                {canDelete && (
                  <Tooltip title='Delete ride'>
                    <IconButton onClick={() => setDeleteModalOpen(true)}>
                      <BiSolidTrash size={20} />
                    </IconButton>
                  </Tooltip>
                )}

                {canRevert && (
                  <Button
                    size='small'
                    variant='contained'
                    color='primary'
                    startIcon={<BiUndo size={16} />}
                    onClick={() => setRevertModalOpen(true)}
                  >
                    Revert
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <CallRideModal
        open={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        ride={ride}
        isRetry={actions.call === 'retry'}
      />

      <GuestRideModal open={editModalOpen} onClose={() => setEditModalOpen(false)} ride={ride} />

      <GuestRideModal open={retryModalOpen} onClose={() => setRetryModalOpen(false)} ride={ride} isRetry />

      <GuestRideDetailsModal open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} ride={ride} />

      <CancelRideModal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} ride={ride} />

      <DeleteRideModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} ride={ride} />

      <RevertRideModal open={revertModalOpen} onClose={() => setRevertModalOpen(false)} ride={ride} />

      <GuestLinkModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        entityId={ride?.id}
        entityType='guestride'
        templateType='guestride.link.shared'
        rooftopName={rooftop?.name ?? undefined}
        showQrMode={false}
        defaultCustomerName={ride?.guest_name ?? undefined}
        defaultCustomerPhone={ride?.guest_phone ?? undefined}
        title='Share Guest Link'
      />
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default GuestRideCard;
